import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useIsFetching } from "@tanstack/react-query";
import { BadgeCheck, Check, Pencil, X } from "lucide-react";
import { useState } from "react";
import { DocumentOcrField } from "../dtos";
import { useStepMutations } from "../hooks/use-step-mutations.hook";
import { formatThousands } from "../utils/format-number";
import { primaryButtonSx } from "./field-shell";

/**
 * §OCR review: the "Extracted fields" block of a document card — WM parity for the CRM tab.
 * Renders the CANONICAL latest-scan values (`document.ocrFields`, where review/confirm/edit live),
 * each with its per-value review chip, an inline pencil edit (PATCH extraction/field — the engine
 * re-opens the confirm state and re-evaluates preconditions), and the Confirm-values control that
 * flips the document's `confirmed` flag (an OCR-gated completion reads exactly this).
 */

const chipTone = (
  status: string | undefined
): { label: string; color: string; bg: string } => {
  switch ((status ?? "PENDING").toUpperCase()) {
    case "APPROVED":
      return { label: "approved", color: "#166534", bg: "#dcfce7" };
    case "REJECTED":
      return { label: "rejected", color: "#b91c1c", bg: "#fee2e2" };
    default:
      return { label: "pending", color: "#6b7280", bg: "#f3f4f6" };
  }
};

const displayValue = (field: DocumentOcrField): string => {
  const { value, type } = field;
  if (value === undefined || value === null || value === "") return "—";
  if ((type === "money" || type === "number") && Number.isFinite(Number(value))) {
    const grouped = formatThousands(String(value));
    return type === "money" ? `EGP ${grouped}` : grouped;
  }
  return String(value);
};

const ExtractionRow = ({
  docKey,
  field,
  editable,
  mutations,
}: {
  docKey: string;
  field: DocumentOcrField;
  editable: boolean;
  mutations: ReturnType<typeof useStepMutations>;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const tone = chipTone(field.reviewStatus);
  const saving =
    mutations.editExtraction.isLoading &&
    mutations.editExtraction.variables?.ocrFieldKey === field.key;

  const commit = () => {
    setEditing(false);
    if (draft === displayValue(field)) return;
    mutations.editExtraction.mutate({
      docKey,
      ocrFieldKey: field.key,
      value: draft,
    });
  };

  return (
    /* §Mobile: the label was a FIXED 150px that could not shrink, so on a ~300px row it took half
       the width; the pencil and the status chip took ~100 more and the value was left with ~50px —
       with the save/cancel buttons living INSIDE that input, there was no room to see what you were
       typing, and the chip ended up drawn under them. On a phone the label now sits on its own line
       and the value + controls get the full width beneath it. Tablet and up is the original row. */
    <Stack
      direction={{ mobile: "column", tablet: "row" }}
      alignItems={{ mobile: "stretch", tablet: "center" }}
      spacing={{ mobile: 0.25, tablet: 1 }}
      sx={{ py: 0.5 }}
    >
      <Typography
        variant="body2"
        sx={{
          width: { mobile: "auto", tablet: 150 },
          flexShrink: { mobile: 1, tablet: 0 },
          color: colors.semantic.text.secondary,
        }}
      >
        {field.label}
      </Typography>
      {/* the value and everything acting on it stay one row at every width — only their
          relationship to the LABEL changes */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flex: 1, minWidth: 0 }}
      >
      {editing ? (
        <TextField
          size="small"
          autoFocus
          fullWidth
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") commit();
            if (event.key === "Escape") setEditing(false);
          }}
          sx={{
            flex: 1,
            // without this the flex item's intrinsic (content) width wins and the field collapses
            minWidth: 0,
            "& .MuiOutlinedInput-root": { borderRadius: theme.nawy.radius.xl },
            // the save/cancel controls live INSIDE the box — keep them from eating the text area
            "& .MuiInputAdornment-root": { ml: 0.5 },
          }}
          InputProps={{
            endAdornment: (
              <Stack direction="row">
                <IconButton size="small" onClick={commit} aria-label="Save value">
                  <Check size={14} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setEditing(false)}
                  aria-label="Cancel edit"
                >
                  <X size={14} />
                </IconButton>
              </Stack>
            ),
          }}
        />
      ) : (
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 600, minWidth: 0 }}>
          {displayValue(field)}
        </Typography>
      )}
      {editable && !editing && (
        <IconButton
          size="small"
          disabled={saving}
          onClick={() => {
            const raw = field.value;
            setDraft(raw === undefined || raw === null ? "" : String(raw));
            setEditing(true);
          }}
          aria-label={`Edit ${field.label}`}
        >
          {saving ? <CircularProgress size={13} /> : <Pencil size={13} />}
        </IconButton>
      )}
      {field.reviewStatus !== undefined && (
        <Box
          component="span"
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            lineHeight: 1,
            px: 1,
            py: 0.5,
            borderRadius: "999px",
            whiteSpace: "nowrap",
            flexShrink: 0,
            color: tone.color,
            backgroundColor: tone.bg,
          }}
        >
          {tone.label}
        </Box>
      )}
      </Stack>
    </Stack>
  );
};

const DocumentExtraction = ({
  docKey,
  fields,
  confirmed,
  editable,
  mutations,
}: {
  docKey: string;
  fields: DocumentOcrField[];
  confirmed: boolean;
  editable: boolean;
  mutations: ReturnType<typeof useStepMutations>;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const refreshing = useIsFetching({ queryKey: ["nawy-now", "step", mutations.stepUid] }) > 0;
  if (!fields.length) return null;
  const confirming =
    mutations.confirmDocExtraction.isLoading &&
    mutations.confirmDocExtraction.variables?.docKey === docKey;

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        border: `1px dashed ${colors.semantic.border.secondary}`,
        borderRadius: theme.nawy.radius.lg,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Extracted fields
        </Typography>
        {refreshing ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CircularProgress size={12} />
            <Typography variant="caption" sx={{ color: colors.semantic.text.secondary }}>
              Updating…
            </Typography>
          </Stack>
        ) : (
          confirmed && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <BadgeCheck size={14} color="#166534" />
              <Typography variant="caption" sx={{ color: "#166534", fontWeight: 600 }}>
                confirmed
              </Typography>
            </Stack>
          )
        )}
      </Stack>
      <Box sx={{ mt: 0.5, opacity: refreshing ? 0.45 : 1 }}>
        {fields.map((field) => (
          <ExtractionRow
            key={field.key}
            docKey={docKey}
            field={field}
            editable={editable && !refreshing}
            mutations={mutations}
          />
        ))}
      </Box>
      {/* §ConfirmBelow (product ask, 2026-08-06): the confirm control follows the values it
          confirms — read first, then sign off. Blue button, not a header link. */}
      {!confirmed && editable && (
        <Button
          size="small"
          variant="contained"
          disabled={confirming || refreshing}
          startIcon={confirming ? <CircularProgress size={13} /> : <Check size={13} />}
          onClick={() => mutations.confirmDocExtraction.mutate({ docKey })}
          sx={{ ...primaryButtonSx(theme), mt: 1 }}
        >
          {confirming ? "Confirming…" : "Confirm values"}
        </Button>
      )}
    </Box>
  );
};

export default DocumentExtraction;
